import ToastStatusGlobal from '@/components/toastStatusGlobal';
import AssetImagesService from '@/modules/assetImage/services/assetImagesService';
import {
  AppCustomizableItems,
  FormAppCustomizableItems,
} from '@/modules/customization/entities/interfaces/customizations.interfaces';
import { AppCustomizationService } from '@/modules/customization/services/appCustomizationService';
import { UiComponentsService } from '@/modules/customization/services/uiComponentsService';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { useRequestCreation } from '@/shared/hooks/useRequestData/useRequestCreation';
import { useRequestDeletion } from '@/shared/hooks/useRequestData/useRequestDeletion';
import { useRequestReadOne } from '@/shared/hooks/useRequestData/useRequestReadOne';
import { useRequestUpdate } from '@/shared/hooks/useRequestData/useRequestUpdate';
import { AxiosError } from 'axios';

const appCustomizationService = new AppCustomizationService();
const uiComponentsService = new UiComponentsService();
const assetImagesService = new AssetImagesService();

export const useCRUDAppCustomization = () => {
  const { company } = useCompany();
  const { toast } = ToastStatusGlobal();

  const {
    data: appCustomization,
    loading: loadingAppCustomization,
    loadData: loadAppCustomization,
  } = useRequestReadOne<AppCustomizableItems | null>({
    source: async () => {
      try {
        return await new AppCustomizationService().showAppCustomizationByCompanyCnpj(company.cnpj);
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });

  const { requestCreate: createAppCustomization, loading: creatingAppCustomization } =
    useRequestCreation<FormAppCustomizableItems, AppCustomizableItems | null>({
      request: async (appCustomizableItems) => {
        if (!company.companyGroupId) {
          return null;
        }

        const response = await appCustomizationService.createAppCustomizationByCompanyCnpj(
          company.cnpj,
          appCustomizableItems,
        );

        toast(
          'Feito',
          'A solicitação de personalização foi enviada e estará disponível no aplicativo em breve.',
          'success',
        );

        return response;
      },
      callback: () => loadAppCustomization(),
    });

  const { requestUpdate: updateAppCustomization, loading: updatingAppCustomization } =
    useRequestUpdate<FormAppCustomizableItems, AppCustomizableItems | null, string>({
      request: async (customizationId, appCustomizableItems) => {
        if (!customizationId) {
          return null;
        }

        const response = await appCustomizationService.updateAppCustomizationById(
          customizationId,
          appCustomizableItems,
        );

        toast(
          'Feito',
          'A solicitação de personalização foi enviada e estará disponível no aplicativo em breve.',
          'success',
        );

        return response;
      },
      callback: () => loadAppCustomization(),
    });

  const { requestDeletion: deleteLogo, loading: deletingLogo } = useRequestDeletion<
    void,
    AppCustomizableItems
  >({
    request: async (appCustomizableItems) => {
      if (appCustomizableItems?.logo?.id) {
        await assetImagesService.deleteAssetImage(appCustomizableItems.logo.id);
        toast('Imagem excluída', 'Imagem excluída com sucesso!', 'success');
      }
    },
    callback: () => loadAppCustomization(),
  });

  const { requestDeletion: deleteBanner, loading: deletingBanner } = useRequestDeletion<
    void,
    AppCustomizableItems
  >({
    request: async (appCustomizableItems) => {
      if (appCustomizableItems?.bannerButton?.id) {
        await uiComponentsService.deleteUiComponent(appCustomizableItems.bannerButton.id);
      }

      if (appCustomizableItems?.banner?.id) {
        await assetImagesService.deleteAssetImage(appCustomizableItems.banner.id);
        toast('Imagem excluída', 'Imagem excluída com sucesso!', 'success');
      }
    },
    callback: () => loadAppCustomization(),
  });

  return {
    loadAppCustomization,
    appCustomization,
    loadingAppCustomization,
    createAppCustomization,
    creatingAppCustomization,
    updateAppCustomization,
    updatingAppCustomization,
    deleteLogo,
    deletingLogo,
    deleteBanner,
    deletingBanner,
  };
};
